import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChartBarIcon,
  EyeIcon,
  FunnelIcon,
  CheckIcon,
  PlayIcon,
  XMarkIcon,
} from 'components/icons';

import { waitlistApi } from '../services/api';
import HowItWorks from '../components/HowItWorks';
import AwardsFeed from '../components/landing/AwardsFeed';

// ===========================================
// Data
// ===========================================


// Each row is a claim a competitor cannot match by rewording their own page.
// The previous rows ("we show what matters", "we help you move faster") were
// contestable by anyone selling anything — and argued Clevor was a better
// contract database, which is the category it is trying to leave.
const differentiators = [
  {
    other: 'Tell you a contract exists',
    clevor: 'Tell you if you are ineligible — and what to fix',
    detail:
      'A set-aside you do not qualify for, a missing certification, a NAICS code that is not on your SAM registration.',
  },
  {
    other: 'Summarize a solicitation',
    clevor: 'Answer from it, citing the section',
    detail:
      'Requirements, evaluation factors and submission instructions pulled out of the package — not paraphrased.',
  },
  {
    other: 'Suggest teaming partners',
    clevor: 'Name real firms with real UEIs',
    detail:
      'Nearest the job site first, with their set-aside certifications — verifiable in SAM before you call.',
  },
];

const productViews = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: ChartBarIcon,
    src: '/shots/dashboard.webp',
    alt: 'The Clevor dashboard: new matches since yesterday, pipeline figures, and upcoming deadlines.',
  },
  {
    id: 'watchlist',
    label: 'Watch List',
    icon: EyeIcon,
    src: '/shots/watchlist.webp',
    alt: 'The Clevor watch list: tracked federal solicitations with days remaining until each deadline.',
  },
  {
    id: 'pipeline',
    label: 'Pipeline',
    icon: FunnelIcon,
    src: '/shots/pipeline.webp',
    alt: 'The Clevor pipeline board: pursuits moving left to right from New through Qualified, Proposal and Submitted.',
  },
];


// ===========================================
// Waitlist / Beta CTA Component
// ===========================================

const BetaSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await waitlistApi.join(email, 'landing-beta-cta');
      setSubmitted(true);
    } catch (err: any) {
      // Never claim success we didn't get — a silent failure here loses the lead.
      setError(
        err?.response?.data?.error ||
          "That didn't go through. Please try again, or email support@clevor.ai.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="text-center">
      {!submitted ? (
        <form onSubmit={handleSubmit} className="max-w-md mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
              aria-label="Email address for early access"
              aria-invalid={!!error}
              className="w-full sm:w-auto flex-1 rounded-lg border-white/20 bg-white/10 text-white placeholder-white/60 px-4 py-3 text-sm focus:ring-2 focus:ring-white/30 focus:border-transparent disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto rounded-lg bg-white px-6 py-3 text-sm font-semibold text-brand-600 hover:bg-brand-50 transition-colors shadow-lg disabled:opacity-70"
            >
              {submitting ? 'Adding you…' : 'Get Early Access'}
            </button>
          </div>
          {error && (
            <p role="alert" className="mt-3 text-sm text-white bg-red-500/25 ring-1 ring-red-300/40 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </form>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center justify-center gap-2 text-white"
        >
          <CheckIcon className="h-5 w-5 text-green-300" />
          <span className="font-medium">Request received. We'll email you when your beta access is ready.</span>
        </motion.div>
      )}
    </div>
  );
};



// ===========================================
// Main Component
// ===========================================

interface PublicStats { open_solicitations: number; agencies: number; documents_read: number; last_import_at: string | null }

function ProofStrip() {
  const [stats, setStats] = useState<PublicStats | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/v1/public/stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && typeof d.open_solicitations === 'number') setStats(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!stats) return null;
  const last = stats.last_import_at ? new Date(stats.last_import_at) : null;
  const items = [
    [stats.open_solicitations.toLocaleString(), 'open federal solicitations right now'],
    [String(stats.agencies), 'agencies, named the way SAM.gov names them'],
    ['Hourly', last ? `pulls from SAM.gov · last ${last.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'pulls from SAM.gov'],
    [stats.documents_read.toLocaleString(), 'solicitation attachments read and searchable'],
  ];
  return (
    <section aria-label="Live figures" className="border-y border-border bg-surface">
      <dl className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-6 px-6 py-8 lg:grid-cols-4 lg:px-8">
        {items.map(([value, label]) => (
          <div key={label} className="min-w-0">
            <dd className="text-2xl font-semibold tracking-tight text-content tabular-nums sm:text-3xl">{value}</dd>
            <dt className="mt-1 text-sm text-content-muted">{label}</dt>
          </div>
        ))}
      </dl>
    </section>
  );
}

const Home: React.FC = () => {
  const [activeView, setActiveView] = useState(0);
  const [userPicked, setUserPicked] = useState(false);

  // Auto-advance until the visitor takes control, then stop for good — cycling
  // out from under someone mid-sentence is worse than never moving. Skipped
  // entirely when the OS asks for reduced motion.
  useEffect(() => {
    if (userPicked) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const interval = setInterval(() => {
      setActiveView((prev) => (prev + 1) % productViews.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [userPicked]);

  const pickView = (i: number) => {
    setUserPicked(true);
    setActiveView(i);
  };

  return (
    <div className="bg-surface">
      {/* ============================================ */}
      {/* Hero Section */}
      {/* ============================================ */}
      <div className="relative isolate overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        {/* Subtle background orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-brand-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '3s' }} />

        <div className="mx-auto max-w-7xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-200 rounded-full px-4 py-1.5 mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                </span>
                <span className="text-sm font-medium text-brand-700">Beta Now Open</span>
              </div>

              {/* Leads on disqualification, not search. Every competitor
                  emails you SAM alerts; almost none tell you a set-aside or a
                  missing registration rules you out before you spend the week.
                  The previous headline sold monitoring, which undersold the
                  product by an entire stage — it drafts the response too. */}
              <h1 className="text-display text-content sm:text-display lg:text-display-lg text-balance">
                Know if you can win it{' '}
                {/* Brand gradient, matching the wordmark and the primary
                    action. Kept to the second clause only — the whole headline
                    in gradient is a different, louder thing. */}
                <span className="gradient-text">before you spend a week on it</span>
              </h1>
              <p className="mt-6 text-lg leading-8 text-content-muted max-w-xl">
                Clevor reads the solicitation, flags what would disqualify you, and drafts what
                it asks for — so the bids you work are the ones you can actually take.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                {/* "Get Early Access" promised something this button does not
                    deliver: it creates an ordinary Free account, and the AI
                    features are granted by hand during the comped beta. A
                    visitor who clicked it landed on a locked product. Say what
                    the click actually does; the beta request lives at the
                    bottom of the page, where it is the only thing on offer. */}
                <Link
                  to="/signup"
                  className="btn-primary text-base px-8 py-3.5 glow-brand hover:glow-brand-strong transition-shadow"
                >
                  Create your free account
                </Link>
                {/* Named "See How It Works" while sending people to the
                    features index. It now goes to the section with that
                    heading, one scroll down. */}
                <a
                  href="#how-it-works"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-content hover:text-brand-600 transition-colors group"
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-surface shadow-md ring-1 ring-border group-hover:ring-brand-300 transition-all">
                    <PlayIcon className="h-4 w-4 text-brand-600 ml-0.5" />
                  </span>
                  See How It Works
                </a>
              </div>
              <p className="mt-4 text-xs text-content-subtle">
                Free account includes opportunity search, match scoring and your watch list. No
                credit card. Proposal drafting and the AI actions are in private beta —{' '}
                <a href="#beta" className="underline hover:text-content">request access below</a>.
              </p>
            </motion.div>

            {/* Right: Product Showcase */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <p className="text-sm text-content-muted text-center mb-3 font-medium">This is what you see instead of logging into SAM.gov.</p>
              <div className="mockup-window rounded-xl overflow-hidden bg-surface">
                {/* Browser chrome */}
                <div className="bg-gray-800 px-4 py-2.5 flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 bg-gray-700 rounded-md px-3 py-1 mx-8">
                    <span className="text-xs text-content-subtle">app.clevor.ai</span>
                  </div>
                </div>

                {/* Tab bar */}
                <div className="bg-surface-sunk border-b border-border px-4 py-2 flex gap-1">
                  {productViews.map((view, i) => (
                    <button
                      key={view.id}
                      onClick={() => pickView(i)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        activeView === i
                          ? 'bg-white text-brand-700 shadow-sm ring-1 ring-border'
                          : 'text-content-muted hover:text-content hover:bg-surface-sunk'
                      }`}
                    >
                      <view.icon className="h-3.5 w-3.5" />
                      {view.label}
                    </button>
                  ))}
                </div>

                {/* Screenshot of the real app, captured by scripts/shoot.js.
                    Full-bleed inside the frame — the browser chrome above is the
                    only border it needs. */}
                <div className="bg-surface-sunk">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeView}
                      src={productViews[activeView].src}
                      alt={productViews[activeView].alt}
                      width={1600}
                      height={1025}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="block w-full h-auto"
                      loading="eager"
                      decoding="async"
                    />
                  </AnimatePresence>
                </div>

                {/* Progress dots */}
                <div className="bg-surface border-t border-border px-4 py-2.5 flex items-center justify-center gap-2">
                  {productViews.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => pickView(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        activeView === i ? 'w-6 bg-brand-500' : 'w-1.5 bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Proof strip: live counts from the corpus. Nothing here is typed in;
          if the numbers cannot be fetched the strip is simply not shown. */}
      <ProofStrip />

      {/* What the government bought in the last two weeks: real award
          notices, named awardees, posted amounts. Hidden when empty. */}
      <AwardsFeed />

      {/* ============================================ */}
      {/* Problem Section */}
      {/* ============================================ */}
      <div className="py-24 sm:py-32 bg-surface-sunk">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 1, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-2xl text-center"
          >
            <h2 className="text-title-lg text-content sm:text-display text-balance">
              You Didn't Start a Business to Refresh SAM.gov
            </h2>
            <div className="mt-8 text-left max-w-lg mx-auto">
              <p className="text-lg text-content-muted mb-4">Most contractors are still:</p>
              <ul className="space-y-3 text-content-muted">
                {[
                  'Logging into SAM.gov every day',
                  'Scanning through irrelevant opportunities',
                  'Missing amendments and deadline changes',
                  'Tracking everything in spreadsheets',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <XMarkIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-content-subtle" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-content-muted">
                It's time-consuming, inconsistent, and easy to miss something important.
              </p>
            </div>
            <p className="mt-8 text-title text-content">
              Clevor replaces the manual work with a system that keeps everything organized and up to date.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ============================================ */}
      {/* How Clevor Works — five stages, each opening onto its features */}
      {/* ============================================ */}
      {/* Was four static cards carrying one sentence each. Accurate, but the
          compliance matrix, past performance library, reviewer pass and
          readiness checklist had nowhere to appear — a visitor could read the
          whole page and not learn they exist. Same ground, opened up. */}
      <HowItWorks />

      {/* ============================================ */}
      {/* Differentiator Section */}
      {/* ============================================ */}
      {/* Sunk ground so it separates from the white stages section above it.
          The page now alternates light → sunk → light → sunk → gradient: four
          background changes, down from eight. */}
      <div className="py-24 sm:py-32 bg-surface-sunk">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 1, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-2xl text-center mb-16"
          >
            <h2 className="text-title-lg text-content sm:text-display text-balance">
              Built to act on, not to browse
            </h2>
            <p className="mt-6 text-lg leading-8 text-content-muted">
              Every claim below is something you can check in the product, not a promise about it.
            </p>
          </motion.div>
          <div className="max-w-3xl mx-auto space-y-4">
            {differentiators.map((diff, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 1, x: i % 2 === 0 ? -12 : 12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-3"
              >
                <div className="bg-surface-sunk rounded-xl p-4 flex items-start gap-3 border border-border">
                  <XMarkIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-content-subtle" aria-hidden="true" />
                  <p className="text-sm text-content-muted">{diff.other}</p>
                </div>
                <div className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-4 flex items-start gap-3 ring-1 ring-brand-200 shadow-sm">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center">
                    <CheckIcon className="h-3.5 w-3.5 text-brand-600" />
                  </div>
                  <div>
                    <p className="text-sm text-brand-800 font-medium">{diff.clevor}</p>
                    {diff.detail && (
                      <p className="mt-1 text-xs text-brand-700/80 leading-relaxed">{diff.detail}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* Beta CTA Section */}
      {/* ============================================ */}
      <div id="beta" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-purple-700" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-1/4 w-96 h-96 bg-surface rounded-full filter blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-purple-300 rounded-full filter blur-3xl" />
        </div>
        <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8 relative">
          <div className="mx-auto max-w-2xl text-center">
            <motion.div
              initial={{ opacity: 1, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-title-lg text-white sm:text-display text-balance">
                Stop guessing which bids are worth the week.
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-brand-100">
                In private beta with GovCon contractors.
              </p>

              {/* Dual CTA: Beta signup OR existing user login */}
              <div className="mt-10 space-y-4">
                <BetaSignup />
                <div className="flex items-center justify-center gap-4 text-sm">
                  <span className="text-brand-200">Already have beta access?</span>
                  <Link to="/login" className="text-white font-semibold hover:text-brand-100 transition-colors underline underline-offset-2">
                    Sign in
                  </Link>
                  <span className="text-brand-300">|</span>
                  <Link to="/pricing" className="text-white font-semibold hover:text-brand-100 transition-colors underline underline-offset-2">
                    View Pricing
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
